import React, { createContext, useContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import { themes, getStoredThemeId, THEME_STORAGE_KEY } from '../../theme';
import { systemTheme as systemThemeUrl, userTheme as userThemeUrl, userThemeSave } from '../BaseURL';

const AppThemeContext = createContext({
  themeId: 'default',
  setThemeId: () => {},
  syncFromBackend: () => {},
  themes,
});

// Read the cached userInfo. Throws are swallowed because this runs on every
// render path and localStorage can be unavailable (e.g. SSR, sandbox).
function readUserInfo() {
  try {
    const raw = localStorage.getItem('userInfo');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

// Login.js stores the staff identifier as `trans_code`, but older builds and
// some auth paths set it as `id`. Read either so the theme system works
// regardless of which session shape is in localStorage.
function userTransCode(user) {
  return user?.trans_code || user?.id || null;
}

export function AppThemeProvider({ children }) {
  // Seed with the locally-cached id so the first paint matches what the user
  // last saw — the network fetch below may overwrite this once it returns.
  const [themeId, setThemeIdState] = useState(() => getStoredThemeId());

  // Tracks whether we've reconciled with the backend yet. Used so the toggle
  // doesn't accidentally fire a redundant POST during the initial sync.
  const hydratingRef = useRef(true);

  const applyTheme = useCallback((id) => {
    if (!themes[id]) return;
    try { localStorage.setItem(THEME_STORAGE_KEY, id); } catch (_) {}
    setThemeIdState(id);
  }, []);

  // Fetch the active theme for the current viewer:
  //   • logged-in user → their saved override, else the system default
  //   • logged-out     → the system default
  // Failures fall back silently to whatever's already on screen so a flaky
  // network never strands the UI on the wrong theme.
  const syncFromBackend = useCallback(async () => {
    const user = readUserInfo();
    const transCode = userTransCode(user);
    try {
      if (transCode) {
        const userRes = await axios.get(userThemeUrl(transCode));
        if (userRes.data?.success && userRes.data.themeId) {
          // User has an explicit per-account override — honour it.
          applyTheme(userRes.data.themeId);
          hydratingRef.current = false;
          return;
        }
      }
      // No per-user override (or anonymous viewer): fall through to the
      // facility default that an admin configured.
      const sysRes = await axios.get(systemThemeUrl);
      if (sysRes.data?.success && sysRes.data.themeId) {
        applyTheme(sysRes.data.themeId);
      }
    } catch (_) {
      // Network/server down — keep whatever localStorage gave us.
    } finally {
      hydratingRef.current = false;
    }
  }, [applyTheme]);

  // Initial sync on mount. Login transitions that happen later trigger a
  // re-sync via the `syncFromBackend` we expose in context (MainLayout calls
  // it once it loads userInfo).
  useEffect(() => {
    syncFromBackend();
  }, [syncFromBackend]);

  // User-initiated theme change. We update the UI immediately and persist to
  // the backend in the background so the toggle stays snappy even on slow
  // connections. The backend write only runs for logged-in users — anonymous
  // viewers still get a local preview via localStorage.
  const setThemeId = useCallback((id) => {
    if (!themes[id]) return;
    applyTheme(id);
    if (hydratingRef.current) return; // skip persistence during the initial hydration
    const user = readUserInfo();
    const transCode = userTransCode(user);
    if (!transCode) return;
    // Persist this user's personal override. It now takes precedence over the
    // admin-configured system default on every future load (see syncFromBackend).
    axios.post(userThemeSave, { transCode, themeId: id }).catch(() => {
      // Persistence failure is non-fatal — local state already updated.
    });
  }, [applyTheme]);

  // Mirror the active theme id onto the document root so global CSS can
  // target hard-coded surfaces with `[data-theme="black"] …` overrides.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = themeId;
    }
  }, [themeId]);

  const muiTheme = themes[themeId]?.theme || themes.default.theme;

  const value = useMemo(
    () => ({ themeId, setThemeId, syncFromBackend, themes }),
    [themeId, setThemeId, syncFromBackend]
  );

  return (
    <AppThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {/* key={themeId} forces the subtree to remount when the user picks a
            new theme, so inline <style> blocks and gradient strings refresh
            instantly without needing a page reload. */}
        <div key={themeId} style={{ display: 'contents' }}>
          {children}
        </div>
      </MuiThemeProvider>
    </AppThemeContext.Provider>
  );
}

export const useAppTheme = () => useContext(AppThemeContext);
